import $ from 'jquery';
import 'slick-carousel';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import History from '@/js/plugins/CookieDb/History';
import { authCheck, zeroPadding, isPc } from '@/js/helpers';
import Favorite from '@/js/plugins/CookieDb/Favorite';
import SmoothScroll from 'smooth-scroll';
import GalleryModal from '@/js/plugins/GalleryModal';
import { Viewer } from '@photo-sphere-viewer/core';
import "@photo-sphere-viewer/core/index.css";

// 画面上端に居座る要素の高さ（ヘッダーバーは追従しないので、追従中の要素のみ数える）
const stickyTopHeight = () => {
    const gnav = document.querySelector('.l-gnav');
    if (!gnav) return 0;
    return getComputedStyle(gnav).position === 'sticky' ? gnav.offsetHeight : 0;
};

// サムネイルのもっと見る表示制御
const renderThumbnailAction = () => {
    let showAll = false;
    const thumbnailLength = $('.pg-gallery-thumbnails__item').not('[data-more]').length;
    if (isPc()) {
        if (thumbnailLength > 8) {
            showAll = true;
        }
    } else {
        if (thumbnailLength > 10) {
            showAll = true;
        }
    }
    if (showAll) {
        $('.pg-gallery-thumbnails__item[data-more]').on('click', (e) => {
            $('.pg-gallery-thumbnails__list').attr('data-render-no-limit', '');
        });
    }
}

// 画像のロード完了を待つ
const initImagesLoad = async (selector) => {
    const slider = document.querySelector(selector);
    const images = Array.from(slider.querySelectorAll('img'));

    // 画像ロードを Promise でラップ
    const loadPromises = images.map(img => {
        return new Promise(resolve => {
            if (img.complete) {
                resolve(); // すでにロード済み
            } else {
                img.addEventListener('load', resolve);
                img.addEventListener('error', resolve); // 失敗でも解決して次へ
            }
        });
    });

    return await Promise.all(loadPromises);
}

// ギャラリー枚数表示（Figma 5478:28928 — 数字14px / スラッシュ12px）
const galleryPaginationHtml = (current, total) =>
    `<span class="pg-gallery__pagination-current">${current}</span>`
    + `<span class="pg-gallery__pagination-sep">/</span>`
    + `<span class="pg-gallery__pagination-total">${total}</span>`;

const setActiveGalleryThumbnail = (index) => {
    const items = Array.from(document.querySelectorAll('.pg-gallery-thumbnails__item'));
    if (!items.length) return;

    items.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        item.classList.toggle('is-active', isActive);
        item.querySelector('.pg-gallery-thumbnails__button')?.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    const current = items[index];
    const list = current?.closest('.pg-gallery-thumbnails__list');
    if (!current || !(list instanceof HTMLElement)) return;

    const left = current.offsetLeft - ((list.clientWidth - current.clientWidth) / 2);
    const maxLeft = Math.max(0, list.scrollWidth - list.clientWidth);
    list.scrollTo({
        left: Math.min(Math.max(0, left), maxLeft),
        behavior: 'smooth',
    });
};

const bindGalleryThumbnailDrag = () => {
    document.querySelectorAll('.pg-gallery-thumbnails__list').forEach((list) => {
        if (list.dataset.dragScrollBound) return;
        list.dataset.dragScrollBound = '1';

        let pointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let hasDragged = false;
        let suppressClick = false;

        const canScroll = () => list.scrollWidth > list.clientWidth;

        list.addEventListener('pointerdown', (event) => {
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            if (!canScroll()) return;

            pointerId = event.pointerId;
            startX = event.clientX;
            startScrollLeft = list.scrollLeft;
            hasDragged = false;
        });

        list.addEventListener('pointermove', (event) => {
            if (pointerId !== event.pointerId) return;

            const delta = event.clientX - startX;
            if (Math.abs(delta) > 8 && !hasDragged) {
                hasDragged = true;
                list.classList.add('is-dragging');
                list.setPointerCapture?.(pointerId);
            }
            if (!hasDragged) return;

            event.preventDefault();
            list.scrollLeft = startScrollLeft - delta;
        });

        const endDrag = (event) => {
            if (pointerId !== event.pointerId) return;
            list.releasePointerCapture?.(pointerId);
            pointerId = null;
            list.classList.remove('is-dragging');
            suppressClick = hasDragged;
        };

        list.addEventListener('pointerup', endDrag);
        list.addEventListener('pointercancel', endDrag);
        list.addEventListener('click', (event) => {
            if (!suppressClick) return;
            event.preventDefault();
            event.stopPropagation();
            suppressClick = false;
        }, true);
    });
};

const getGalleryCaptions = (slider) => Array.from(slider?.querySelectorAll('figure') || [])
    .map((figure) => figure.dataset.galleryCaption || figure.querySelector('img')?.alt || '');

const setGalleryCaption = (captions, index) => {
    const caption = document.querySelector('.js-gallery-caption');
    if (!caption) return;

    const text = captions[index] || '';
    caption.textContent = text;
    caption.hidden = text === '';
};

const bindGalleryThumbnails = ($slider) => {
    document.querySelectorAll('.pg-gallery-thumbnails__item').forEach((item, index) => {
        item.querySelector('.pg-gallery-thumbnails__button')?.addEventListener('click', (e) => {
            e.preventDefault();
            if ($slider.hasClass('slick-initialized')) {
                $slider.slick('slickGoTo', index);
                return;
            }
            setActiveGalleryThumbnail(index);
        });
    });
};

// ギャラリーのアクション設定
const setGalleryAction = async () => {
    renderThumbnailAction()
    let slideAllCount = 0;

    await initImagesLoad('.js-gallery-slider')
    .then(() => {
        const $slider = $(".js-gallery-slider");
        const captions = getGalleryCaptions($slider.get(0));
        bindGalleryThumbnails($slider);
        bindGalleryThumbnailDrag();
        const slideCount = $slider.find('.slick-slide').length || $slider.children().length;

        // 1枚の場合は初期化しない
        if (slideCount <= 1) {
            $('.pg-gallery__pagination').html(galleryPaginationHtml(1, 1));
            setActiveGalleryThumbnail(0);
            setGalleryCaption(captions, 0);
            return;
        }

        // スライダーアクション
        $slider.on('init', (event, slick) => {
            slideAllCount = slick.slideCount;
            $('.pg-gallery__pagination').html(galleryPaginationHtml(1, slideAllCount));
            setActiveGalleryThumbnail(0);
            setGalleryCaption(captions, 0);
        })
        .slick({
            fade: true,
            slidesToShow: 1,
            autoplay: false,
            speed: 1000,
            infinite: true,
            arrows: false,
        })
        .on('afterChange', (event, slick, currentSlide) => {
            $('.pg-gallery__pagination').html(galleryPaginationHtml(currentSlide + 1, slideAllCount));
            setActiveGalleryThumbnail(currentSlide);
            setGalleryCaption(captions, currentSlide);
        });

        // カスタムナビゲーション
        const prevBtn = document.querySelector('.pg-gallery__nav-prev');
        const nextBtn = document.querySelector('.pg-gallery__nav-next');
        if (prevBtn) {
            prevBtn.addEventListener('click', () => $slider.slick('slickPrev'));
        }
        if (nextBtn) {
            nextBtn.addEventListener('click', () => $slider.slick('slickNext'));
        }
    });
}

// 物件詳細の追加表示（SP）
const showPropertyDetail = () => {
    $('.pg-estate-detail__more a').on('click', (e) => {
        $('.pg-estate-detail').addClass('is-open');
    })
}

const setFavoriteAction = async (pageId) => {
    const targetObjs = document.querySelectorAll('[data-type="favorite"]');
    const favorite = new Favorite();
    const targetDb = authCheck() ? 'db' : 'cookie';
    const changeFavoriteState = (target, newState) => {
        if (newState) {
            target.dataset.active = '';
        } else {
            delete target.dataset.active;
        }
        document.querySelectorAll('[data-favorite-count]').forEach((targetObj) => {
            const baseCount = targetObj.dataset.count ? Number(targetObj.dataset.count) : 0;
            const count = Math.max(0, newState ? baseCount + 1 : baseCount - 1);
            targetObj.textContent = count < 99 ? String(count) : '99+';
            targetObj.dataset.count = String(count);
            targetObj.hidden = count <= 0;
        });
        document.querySelectorAll(`[data-type="favorite"][data-id="${pageId}"], .c-buttons-favorite[data-id="${pageId}"]`).forEach((targetObj) => {
            if (newState) {
                targetObj.dataset.active = '';
            }
            else {
                delete targetObj.dataset.active;
            }
        });
    }

    targetObjs.forEach((obj) => {
        obj.addEventListener('click', async (e) => {
            const isFavorite = e.currentTarget.hasAttribute('data-active');
            changeFavoriteState(e.currentTarget, !isFavorite);

            const resp = await favorite.setState(targetDb, pageId, !isFavorite)
            if (!resp) {
                changeFavoriteState(e.currentTarget, isFavorite);
            }
        })
    })
}

const setOpenHouseAction = () => {
    if (document.querySelector('.pg-tour-body') === null) return;
    document.querySelector('.pg-tour-body__more').addEventListener('click', (e) => {
        document.querySelector('.pg-tour__body').dataset.open = '';
    });
    // コンテンツの縦サイズが親の高さを超えていたら「もっと見る」ボタンを表示
    const tourBody = document.querySelector('.pg-tour__body');
    console.log(tourBody.scrollHeight, tourBody.clientHeight)
    if (tourBody.scrollHeight > tourBody.clientHeight) {
        document.querySelector('.pg-tour__body').dataset.more = '';
    }
}

// 360度画像の表示
export const set360Image = () => {
    if(!document.querySelector('.pg-panorama')) return;
    const firstItem = document.querySelector('.pg-panorama__item');
    const viewer = new Viewer({
        container: document.querySelector('.pg-panorama__viewer'),
        panorama: firstItem.dataset.url || '',
        defaultZoomLvl: 0, // いちばんズームアウト
    });

    document.querySelectorAll('.pg-panorama__item').forEach(item => {
        item.addEventListener('click', (e) => {
            const url = e.currentTarget.dataset.url;
            viewer.setPanorama(url);

            // クリックされたアイテムをアクティブにする
            document.querySelectorAll('.pg-panorama__item').forEach(el => {
                delete el.dataset.active;
            });
            e.currentTarget.dataset.active = '';
        });
    });
    document.querySelector('.pg-panorama__item').dataset.active = '';
}


/**
 * ページ内ナビ スクロール制御（SP のみ）
 * 下スクロール: data-hidden を付与して非表示
 * 上スクロール: data-hidden を除去して表示
 */
const BP_SM = 640; // @include sm の閾値

const getPageNav = () =>
    document.querySelector('.pg-lp-anchor')
    || document.querySelector('.pg-content-navi')
    || document.querySelector('.pg-nav');

const setNavScroll = () => {
    const nav = getPageNav();
    if (!nav) return;

    const mq = window.matchMedia(`(max-width: ${BP_SM}px)`);
    let lastY = window.scrollY;

    const onScroll = () => {
        if (!mq.matches) return;

        const currentY = window.scrollY;
        const isAtTop = currentY === 0;
        const isScrollingDown = currentY > lastY && currentY > nav.offsetHeight;

        nav.toggleAttribute('data-hidden', !isAtTop && isScrollingDown);
        lastY = currentY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
};

const setLpAnchorReveal = () => {
    const nav = document.querySelector('.pg-lp-anchor');
    const badge = document.querySelector('.pg-area-badge');
    // LP版（/lp/{id}）はヒーローがページ最上部。基準を badge のままにすると
    // badge はヒーローより上にあるため、ヒーローを見ている最中にナビが出てしまう。
    // ヒーローがあるときだけ「その下端」を基準にする（通常詳細は従来どおり badge 上端）
    const hero = document.querySelector('.pg-lp-intro__hero');
    const anchorEl = hero || badge;
    if (!nav || !anchorEl) return;

    const update = () => {
        const headerH = stickyTopHeight();
        const rect = anchorEl.getBoundingClientRect();
        const edge = hero ? rect.bottom : rect.top;
        // 基準がヘッダー下に入る距離に応じて 0→1（ナビ高さ分かけて徐々に露出）
        const navH = Math.max(nav.offsetHeight, 1);
        const raw = (headerH - edge) / navH;
        const progress = Math.min(1, Math.max(0, raw));

        // ヘッダー高さ（PC64 / SP48）に追従させて上余白をなくす
        nav.style.setProperty('--pg-lp-anchor-top', `${headerH}px`);
        nav.style.setProperty('--pg-lp-anchor-progress', String(progress));
        nav.classList.toggle('is-visible', progress > 0.02);
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
};

const setNavScrollSpy = () => {
    const nav = getPageNav();
    if (!nav) return;

    const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
    if (!links.length) return;

    // 同一ハッシュが複数ある場合は先頭リンクのみ spy 対象にする
    const seen = new Set();
    const sections = [];
    links.forEach((link) => {
        const id = link.getAttribute('href')?.slice(1);
        if (!id || seen.has(id)) return;
        const section = document.getElementById(id);
        if (!section) return;
        seen.add(id);
        sections.push({ id, section, links: links.filter((l) => l.getAttribute('href') === `#${id}`) });
    });

    if (!sections.length) return;

    // 横スクロール: 原則は現在地を左端へ。
    // ただし末尾で他ボタンが消える位置までは進めない（右側余白の強制確保はしない）。
    const list = nav.querySelector('.pg-lp-anchor__list') || nav;
    if (list instanceof HTMLElement) {
        list.style.paddingRight = '';
    }

    const scrollCurrentTowardLeft = (link) => {
        if (!link || !(list instanceof HTMLElement)) return;
        if (list.scrollWidth <= list.clientWidth + 1) return;

        const padLeft = parseFloat(getComputedStyle(list).paddingLeft) || 0;
        const delta = link.getBoundingClientRect().left
            - list.getBoundingClientRect().left
            - padLeft;
        const target = list.scrollLeft + delta;
        const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
        // 左寄せしたいが、リスト末端を超えて空振りしない（＝他ボタンを消さない）
        const next = Math.min(Math.max(0, target), maxScroll);
        if (Math.abs(next - list.scrollLeft) < 1) return;
        list.scrollTo({ left: next, behavior: 'smooth' });
    };

    let currentId = null;

    const update = () => {
        const headerH = stickyTopHeight();
        const navH = nav.offsetHeight || 0;
        const threshold = headerH + navH + 8;

        let active = sections[0];
        for (const s of sections) {
            if (s.section.getBoundingClientRect().top <= threshold) {
                active = s;
            }
        }

        links.forEach((link) => link.classList.remove('is-current', 'is-active'));
        active.links.forEach((link) => {
            link.classList.add('is-current');
            link.classList.add('is-active');
        });

        if (active.id !== currentId) {
            currentId = active.id;
            scrollCurrentTowardLeft(active.links[0]);
        }
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
};

const main = async () => {
    if (!document.querySelector('#page-estate-detail') && !document.querySelector('#page-rich')) return;
    const history = new History();
    const galleryInput = document.querySelector('input[name="gallery_images"]');
    const images = galleryInput
        ? JSON.parse(galleryInput.getAttribute('value') || '[]')
        : [];
    const madoriInput = document.querySelector('input[name="madori_images"]');
    const madoriImages = madoriInput
        ? JSON.parse(madoriInput.getAttribute('value') || '[]')
        : [];

    const toGalleryImgList = (list) => (list || [])
        .map((img) => {
            if (typeof img === 'string') {
                return { src: img, alt: '' };
            }
            return {
                src: img.imagePath || img.src || '',
                alt: img.comment || img.title || img.alt || '',
            };
        })
        .filter((img) => img.src);

    let activeGalleryModal = null;
    const openGalleryModal = (list, index = 0, mode = 'single') => {
        const imgList = toGalleryImgList(list);
        if (!imgList.length) return;

        activeGalleryModal?.destroy?.();
        activeGalleryModal = new GalleryModal({
            overlayClose: true,
            imgList,
            mode,
        });
        window.setTimeout(() => {
            activeGalleryModal.show(index);
        }, 50);
    };

    const pageId = document.querySelector('main').dataset.id;
    if (pageId.match(/^[0-9]+$/)) {
        // 閲覧履歴の記録
        history.setState(authCheck() ? 'db' : 'cookie', pageId, true)
    }

    // クリックした画像をモーダル表示
    document.querySelectorAll('.pg-gallery__main-slider img').forEach((img, index) => {
        img.addEventListener('click', () => {
            openGalleryModal(images, index);
        })
    });

    document.querySelectorAll('.pg-gallery-thumbnails__list img').forEach((img, index) => {
        img.addEventListener('click', (e) => {
            // galleryModal.show(index)
        })
    });
    if (document.querySelectorAll('.pg-gallery__content .pg-gallery__all-view')) {
        document.querySelectorAll('.pg-gallery__content .pg-gallery__all-view').forEach((button) => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                openGalleryModal(images, 0, 'list');
            });
        });
    }

    // 間取り図モーダル
    document.querySelectorAll('.pg-gallery__tool[data-type="floorplan"]').forEach((button) => {
        if (button.tagName === 'A') return;
        button.addEventListener('click', (e) => {
            e.preventDefault();
            openGalleryModal(madoriImages, 0);
        });
    });

    // 区画図・外観・間取りカードなど単画像 → 共通モーダル
    document.querySelectorAll('[data-modal-image]').forEach((obj) => {
        obj.addEventListener('click', (e) => {
            // 前後矢印のクリックはモーダルを開かない
            if (e.target.closest('.js-structure-prev, .js-structure-next, .js-exterior-prev, .js-exterior-next')) {
                return;
            }
            const src = e.currentTarget.dataset.modalImage;
            if (!src) return;
            const alt = e.currentTarget.getAttribute('alt')
                || e.currentTarget.closest('article, figure')?.querySelector('img')?.alt
                || '';
            openGalleryModal([{ src, alt }], 0);
        });
    });

    // 戻るボタン
    $('.pg-fix-content__item--back a, .pg-back a').on('click', (e) => {
        e.preventDefault();
        window.history.back();
    });

    const scrollOffset = () => {
        const headerH = stickyTopHeight();
        const nav = document.querySelector('.pg-lp-anchor')
            || document.querySelector('.pg-content-navi');
        const navVisible = nav?.classList.contains('is-visible')
            || nav?.classList.contains('pg-content-navi');
        const navH = navVisible ? (nav?.offsetHeight || 0) : 0;
        return headerH + navH;
    };

    new SmoothScroll('a[href*="#"]', {
        speed: 300,
        // ヘッダー＋（表示中の）ページ内ナビ分をオフセット
        offset: scrollOffset,
        speedAsDuration: true,
        easing: 'linear',
    });

    setOpenHouseAction();
    showPropertyDetail();
    setGalleryAction();
    setFavoriteAction(pageId);
    set360Image();
    setIntroductionActions();
    setExteriorActions();
    setStructureActions();
    setLoanSimulator();
    setLoanModal();
    setLoanHelpTip();
    setLpAnchorReveal();
    setNavScrollSpy();
}

const bindLoanSimulator = (root) => {
    if (!root) return;

    const priceEl = root.querySelector('[data-loan-price]');
    const rateEl = root.querySelector('[data-loan-rate]');
    const yearsEl = root.querySelector('[data-loan-years]');
    const downEl = root.querySelector('[data-loan-down]');
    const bonusEl = root.querySelector('[data-loan-bonus]');
    const borrowEl = root.querySelector('[data-loan-borrow]');
    const monthlyEl = root.querySelector('[data-loan-monthly]');

    const formatMan = (n) => {
        const v = Math.max(0, Number(n) || 0);
        return Math.round(v).toLocaleString('ja-JP');
    };

    const formatMonthly = (n) => {
        const v = Math.max(0, Number(n) || 0);
        return (Math.round(v * 10) / 10).toLocaleString('ja-JP', {
            minimumFractionDigits: v % 1 === 0 ? 0 : 1,
            maximumFractionDigits: 1,
        });
    };

    const calc = () => {
        const price = Math.max(0, Number(priceEl?.value) || 0); // 万円
        const ratePct = Math.max(0, Number(rateEl?.value) || 0);
        const years = Math.max(1, Number(yearsEl?.value) || 35);
        const down = Math.max(0, Number(downEl?.value) || 0); // 万円
        const bonus = Math.max(0, Number(bonusEl?.value) || 0); // 万円/回（年2回想定）

        const borrow = Math.max(0, price - down);
        // ボーナス返済分を元本から概算控除（年2回 × 年数）
        const bonusTotal = bonus * 2 * years;
        const principalMan = Math.max(0, borrow - bonusTotal);
        const principalYen = principalMan * 10000;
        const n = years * 12;
        const r = ratePct / 100 / 12;

        let monthlyYen = 0;
        if (principalYen > 0) {
            if (r === 0) {
                monthlyYen = principalYen / n;
            } else {
                const pow = (1 + r) ** n;
                monthlyYen = (principalYen * r * pow) / (pow - 1);
            }
        }

        if (borrowEl) borrowEl.textContent = formatMan(borrow);
        if (monthlyEl) monthlyEl.textContent = formatMonthly(monthlyYen / 10000);
    };

    [priceEl, rateEl, yearsEl, downEl, bonusEl].forEach((el) => {
        if (!el) return;
        el.addEventListener('input', calc);
        el.addEventListener('change', calc);
    });

    calc();
};

const setLoanSimulator = () => {
    document.querySelectorAll('[data-loan-root]').forEach((root) => bindLoanSimulator(root));
};

const setLoanHelpTip = () => {
    const closeAll = () => {
        document.querySelectorAll('.pg-loan__help-tip').forEach((tip) => {
            tip.setAttribute('hidden', '');
        });
        document.querySelectorAll('[data-loan-help]').forEach((btn) => {
            btn.setAttribute('aria-expanded', 'false');
        });
    };

    document.querySelectorAll('[data-loan-help]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const tip = btn.parentElement?.querySelector('.pg-loan__help-tip');
            if (!tip) return;
            const willOpen = tip.hasAttribute('hidden');
            closeAll();
            if (willOpen) {
                tip.removeAttribute('hidden');
                btn.setAttribute('aria-expanded', 'true');
            }
        });
    });

    document.querySelectorAll('.pg-loan__help-tip').forEach((tip) => {
        tip.addEventListener('click', (e) => e.stopPropagation());
    });

    document.addEventListener('click', closeAll);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAll();
    });
};

const setLoanModal = () => {
    const modal = document.querySelector('[data-loan-modal]');
    if (!modal) return;

    const dialog = modal.querySelector('.pg-loan-modal__dialog');
    const priceSelect = dialog?.querySelector('[data-loan-price]');

    const open = (price) => {
        if (price != null && price !== '' && priceSelect) {
            const target = String(price);
            const match = [...priceSelect.options].find(
                (opt) => opt.value === target || Number(opt.value) === Number(target)
            );
            if (match) {
                priceSelect.value = match.value;
                priceSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
        modal.setAttribute('data-active', 'true');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        modal.querySelector('.pg-loan-modal__close')?.focus();
    };

    const close = () => {
        modal.setAttribute('data-active', 'false');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
    };

    modal.querySelectorAll('[data-loan-modal-close]').forEach((el) => {
        el.addEventListener('click', close);
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || modal.getAttribute('data-active') !== 'true') return;
        // 金利チップ表示中はモーダル本体は閉じない
        if (modal.querySelector('.pg-loan__help-tip:not([hidden])')) return;
        close();
    });

    document.querySelectorAll('.js-loan-modal-open').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            open(btn.dataset.loanPrice);
        });
    });
};

const setStructureActions = () => {
    const root = document.querySelector('.pg-structure');
    if (!root) return;

    const tabList = root.querySelector('.pg-structure__tabs');
    const track = root.querySelector('[data-structure-track]');
    const cards = [...root.querySelectorAll('[data-structure-card]')];
    const tabs = [...root.querySelectorAll('.pg-structure__tab')];
    const thumb = root.querySelector('[data-structure-thumb]');

    // タブクリック直後は syncFromScroll に上書きされないようロック
    let lockIndex = null;
    let lockTimer = null;

    // 追従ナビ/外観タブと同様: 選択中タブを左端へ寄せる
    const scrollTabTowardLeft = (tab) => {
        if (!tab || !(tabList instanceof HTMLElement)) return;
        if (tabList.scrollWidth <= tabList.clientWidth + 1) return;

        const padLeft = parseFloat(getComputedStyle(tabList).paddingLeft) || 0;
        const delta = tab.getBoundingClientRect().left
            - tabList.getBoundingClientRect().left
            - padLeft;
        const target = tabList.scrollLeft + delta;
        const maxScroll = Math.max(0, tabList.scrollWidth - tabList.clientWidth);
        const next = Math.min(Math.max(0, target), maxScroll);
        if (Math.abs(next - tabList.scrollLeft) < 1) return;
        tabList.scrollTo({ left: next, behavior: 'smooth' });
    };

    const activateTab = (index) => {
        if (!Number.isFinite(index) || index < 0 || index >= Math.max(tabs.length, cards.length)) return;
        tabs.forEach((tab, i) => {
            const on = i === index;
            tab.classList.toggle('is-active', on);
            tab.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        scrollTabTowardLeft(tabs[index]);
    };

    const scrollToCard = (index, { smooth = false } = {}) => {
        const card = cards[index];
        if (!card || !track) return;

        lockIndex = index;
        activateTab(index);

        const trackRect = track.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        const left = track.scrollLeft + (cardRect.left - trackRect.left);
        // タブで大きく飛ぶときは instant（smooth だと途中の号棟が一瞬見える）
        track.scrollTo({
            left: Math.max(0, left),
            behavior: smooth ? 'smooth' : 'auto',
        });

        const clearLock = () => {
            if (lockIndex === index) lockIndex = null;
            track.removeEventListener('scrollend', clearLock);
        };
        window.clearTimeout(lockTimer);
        track.addEventListener('scrollend', clearLock, { once: true });
        // scrollend 非対応ブラウザ向けフォールバック
        lockTimer = window.setTimeout(clearLock, smooth ? 500 : 50);
    };

    const syncFromScroll = () => {
        if (!track || !cards.length) return;
        const left = track.scrollLeft;
        const max = Math.max(0, track.scrollWidth - track.clientWidth);

        let nearest = 0;
        if (lockIndex !== null) {
            nearest = lockIndex;
        } else if (max > 0 && left >= max - 2) {
            // 終端は最後のカードを左端に揃えられないため、末尾を選択
            nearest = cards.length - 1;
        } else {
            let best = Infinity;
            cards.forEach((card, i) => {
                const dist = Math.abs(card.offsetLeft - left);
                if (dist < best) {
                    best = dist;
                    nearest = i;
                }
            });
        }

        const prevActive = tabs.findIndex((t) => t.classList.contains('is-active'));
        tabs.forEach((tab, i) => {
            const on = i === nearest;
            tab.classList.toggle('is-active', on);
            tab.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        // 選択が変わったときだけタブを左寄せ（スクロール毎の重複を避ける）
        if (nearest !== prevActive) {
            scrollTabTowardLeft(tabs[nearest]);
        }

        if (thumb) {
            const ratio = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
            const thumbPct = Math.min(Math.max(ratio * 100, 16), 100);
            const progress = max > 0 ? left / max : 0;
            const maxMove = (100 - thumbPct) / thumbPct;
            thumb.style.width = `${thumbPct}%`;
            thumb.style.transform = `translateX(${progress * maxMove * 100}%)`;
        }
    };

    if (tabList) {
        tabList.addEventListener('click', (e) => {
            const tab = e.target.closest('.pg-structure__tab');
            if (!tab || !tabList.contains(tab)) return;
            e.preventDefault();
            e.stopPropagation();
            scrollToCard(tabs.indexOf(tab));
        });
    }

    // キャプチャ段階で受け取り、画像モーダルのクリックと競合しないようにする
    root.addEventListener('click', (e) => {
        if (e.target.closest('.pg-structure__tab')) return;

        const prevBtn = e.target.closest('.js-structure-prev');
        const nextBtn = e.target.closest('.js-structure-next');
        if (!prevBtn && !nextBtn) return;

        const btn = prevBtn || nextBtn;
        // 矢印はカード間の移動ではなく、そのカード内の画像切替に使う
        const media = btn.closest('.pg-structure-card__media');
        if (!media) return;

        let images = [];
        try {
            images = JSON.parse(media.dataset.structureImages || '[]');
        } catch {
            images = [];
        }
        if (images.length < 2) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const current = Number(media.dataset.structureImageIndex) || 0;
        const next = (current + (prevBtn ? -1 : 1) + images.length) % images.length;
        media.dataset.structureImageIndex = String(next);

        const src = images[next];
        const image = media.querySelector('.pg-structure-card__image');
        if (image) {
            image.src = src;
            // 拡大モーダルはクリック時に dataset を読むので、切替後の画像に追従させる
            image.dataset.modalImage = src;
        }
        const zoom = media.querySelector('.pg-structure-card__zoom');
        if (zoom) {
            zoom.dataset.modalImage = src;
        }
    }, true);

    if (track) {
        track.addEventListener('scroll', syncFromScroll, { passive: true });
        syncFromScroll();
    }
};

const setExteriorActions = () => {
    const root = document.querySelector('.pg-exterior');
    if (!root) return;

    const tabList = root.querySelector('.pg-exterior__tabs');
    const tabs = [...root.querySelectorAll('.pg-exterior__tab')];
    const panels = [...root.querySelectorAll('.pg-exterior__panel')];

    // 追従ナビと同様: 選択中タブを左端へ寄せる（末端は maxScroll で打ち止め）
    const scrollTabTowardLeft = (tab) => {
        if (!tab || !(tabList instanceof HTMLElement)) return;
        if (tabList.scrollWidth <= tabList.clientWidth + 1) return;

        const padLeft = parseFloat(getComputedStyle(tabList).paddingLeft) || 0;
        const delta = tab.getBoundingClientRect().left
            - tabList.getBoundingClientRect().left
            - padLeft;
        const target = tabList.scrollLeft + delta;
        const maxScroll = Math.max(0, tabList.scrollWidth - tabList.clientWidth);
        const next = Math.min(Math.max(0, target), maxScroll);
        if (Math.abs(next - tabList.scrollLeft) < 1) return;
        tabList.scrollTo({ left: next, behavior: 'smooth' });
    };

    const activate = (index) => {
        if (!Number.isFinite(index) || index < 0 || index >= panels.length) return;

        tabs.forEach((tab, i) => {
            const on = i === index;
            tab.classList.toggle('is-active', on);
            tab.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panels.forEach((panel, i) => {
            const on = i === index;
            panel.classList.toggle('is-active', on);
            panel.hidden = !on;
            if ('inert' in panel) panel.inert = !on;
        });
        scrollTabTowardLeft(tabs[index]);
    };

    // クリックされたボタン要素からインデックスを取る（ずれ防止）
    if (tabList) {
        tabList.addEventListener('click', (e) => {
            const tab = e.target.closest('.pg-exterior__tab');
            if (!tab || !tabList.contains(tab)) return;
            e.preventDefault();
            e.stopPropagation();
            activate(tabs.indexOf(tab));
        });
    }

    // キャプチャ段階で受け取り、画像モーダルのクリックと競合しないようにする
    root.addEventListener('click', (e) => {
        if (e.target.closest('.pg-exterior__tab')) return;

        const prevBtn = e.target.closest('.js-exterior-prev');
        const nextBtn = e.target.closest('.js-exterior-next');
        if (!prevBtn && !nextBtn) return;

        // 非表示パネル内の矢印は無視（ヒットずれ対策）
        const btn = prevBtn || nextBtn;
        const panel = btn.closest('.pg-exterior__panel');
        if (!panel || panel.hidden || !panel.classList.contains('is-active')) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const current = panels.indexOf(panel);
        if (prevBtn) {
            activate((current - 1 + panels.length) % panels.length);
        } else {
            activate((current + 1) % panels.length);
        }
    }, true);

    // 初期状態の inert を同期
    const initial = Math.max(0, panels.findIndex((p) => p.classList.contains('is-active')));
    panels.forEach((panel, i) => {
        if ('inert' in panel) panel.inert = i !== initial;
    });
};

const setIntroductionActions = () => {
    // アピールポイント（.pg-introduction-text）・スタッフコメント（.pg-staff-comment__collapse）・
    // オープンハウス・イベント情報（.pg-open-house__collapse）の「もっと見る」
    document.querySelectorAll('.js-intro-text-more').forEach((moreBtn) => {
        moreBtn.addEventListener('click', () => {
            const box = moreBtn.closest('[data-collapsed]');
            if (!box) return;
            const label = moreBtn.querySelector('.js-intro-text-more-label') || moreBtn.querySelector('span');
            const open = box.hasAttribute('data-open');
            if (open) {
                box.removeAttribute('data-open');
                if (label) label.textContent = 'もっと見る';
                moreBtn.setAttribute('aria-expanded', 'false');
            } else {
                box.setAttribute('data-open', '');
                if (label) label.textContent = '閉じる';
                moreBtn.setAttribute('aria-expanded', 'true');
            }
        });

        // 3行に収まっていて畳む必要が無いときはボタンを出さない（スタッフコメントは短いことが多い）。
        // 畳んでいる間だけ測る。PC/SPで空行の扱いが変わるので幅が変わったら測り直す
        const body = moreBtn.closest('[data-collapsed]')?.querySelector('.pg-introduction-text__body, .pg-staff-comment__text, .pg-open-house__text');
        if (!body) return;
        const syncVisibility = () => {
            if (moreBtn.closest('[data-collapsed]').hasAttribute('data-open')) return;
            moreBtn.style.display = body.scrollHeight > body.clientHeight + 1 ? '' : 'none';
        };
        syncVisibility();
        // Webフォントの読み込みで行の折り返しが変わるので、読み込み後にも測る
        window.addEventListener('load', syncVisibility);
        window.addEventListener('resize', syncVisibility);
    });

    // シェアボタンの挙動は components/buttons/_share.js（吹き出しメニュー）が担う
};

export default main;
